import {BackendLinesDeltaQueryQuery} from '../../../../generated/graphql';
import {BucketedLine} from '../../types';
import {
  applyBackendLinesDelta,
  applyDelta,
  applyGroupedBackendLinesDelta,
  applyGroupedDelta,
} from './applyDeltas';
import {
  doBackendLinesApiQuery,
  doBackendLinesApiQueryByInternalId,
  doBucketedApiQuery,
  doBucketedApiQueryByInternalId,
  doGroupedBackendLinesApiQuery,
  doGroupedBucketedApiQuery,
  doGroupedBucketedApiQueryByInternalId,
  doShadowBucketedApiQuery,
} from './execution';
import {mergeBaselineAndPinnedRuns} from './mergeBaselineAndPinned';
import {postProcessGroupedRuns, postProcessRuns} from './postProcessRuns';
import {QueryVars} from './queryVars';
import {
  BackendLinesQueryResult,
  BackendLinesQueryRuns,
  BaseQueryBehavior,
  BucketedQueryResult,
  BucketedQueryRuns,
  GroupedBackendLinesQueryResult,
  GroupedBackendLinesQueryRuns,
  GroupedBucketedQueryResult,
  GroupedBucketedQueryRuns,
} from './types';
import {isAggregatedRuns} from './utils';

type BackendLinesQueryBehaviorType = BaseQueryBehavior<
  BackendLinesQueryResult,
  BackendLinesQueryRuns
>;

type GroupedBackendLinesQueryBehaviorType = BaseQueryBehavior<
  GroupedBackendLinesQueryResult,
  GroupedBackendLinesQueryRuns
>;

export const ShadowedBackendLinesQueryBehavior: BackendLinesQueryBehaviorType =
  {
    query: doShadowBucketedApiQuery,
    unpack: (result: BackendLinesQueryResult) => {
      // Only merge if baseline or pinned runs are present (feature flag gated)
      if (
        result?.data?.project?.baselineRun ||
        result?.data?.project?.pinnedRuns
      ) {
        mergeBaselineAndPinnedRuns(result);
      }
      return result?.data?.project?.runs;
    },
    postProcess: null,
    applyDeltaFn: (vars: {
      currentRunsById: Record<string, BucketedLine>;
      runs: BackendLinesQueryRuns | null | undefined;
      specs: QueryVars['bucketedHistorySpecs'];
    }) => {
      const deltas = vars.runs?.deltas?.delta ?? [];
      const order = vars.runs?.deltas?.order ?? [];
      return applyBackendLinesDelta({
        currentRunsById: vars.currentRunsById,
        order,
        delta: deltas,
      });
    },
  };

// TODO: change this to backend lines query
export const BackendLinesQueryBehavior: BackendLinesQueryBehaviorType = {
  query: doBackendLinesApiQuery,
  unpack: (result: BackendLinesQueryResult) => {
    // Only merge if baseline or pinned runs are present (feature flag gated)
    if (
      result?.data?.project?.baselineRun ||
      result?.data?.project?.pinnedRuns
    ) {
      mergeBaselineAndPinnedRuns(result);
    }
    return result?.data?.project?.runs;
  },
  postProcess: null,
  applyDeltaFn: (vars: {
    currentRunsById: Record<string, BucketedLine>;
    runs: BackendLinesQueryRuns | null | undefined;
    specs: QueryVars['bucketedHistorySpecs'];
  }) => {
    const deltas = vars.runs?.deltas?.delta ?? [];
    const order = vars.runs?.deltas?.order ?? [];
    return applyBackendLinesDelta({
      currentRunsById: vars.currentRunsById,
      order,
      delta: deltas,
    });
  },
};

// TODO: change this to backend lines query
export const BackendLinesIdQueryBehavior: BackendLinesQueryBehaviorType = {
  query: doBackendLinesApiQueryByInternalId,
  unpack: (result: BackendLinesQueryResult) => {
    // Only merge if baseline or pinned runs are present (feature flag gated)
    if (
      result?.data?.project?.baselineRun ||
      result?.data?.project?.pinnedRuns
    ) {
      mergeBaselineAndPinnedRuns(result);
    }
    return result?.data?.project?.runs;
  },
  postProcess: null,
  applyDeltaFn: (vars: {
    currentRunsById: Record<string, BucketedLine>;
    runs: BackendLinesQueryRuns | null | undefined;
    specs: QueryVars['bucketedHistorySpecs'];
  }) => {
    const deltas = vars.runs?.deltas?.delta ?? [];
    const order = vars.runs?.deltas?.order ?? [];
    return applyBackendLinesDelta({
      currentRunsById: vars.currentRunsById,
      order,
      delta: deltas,
    });
  },
};

export const GroupedBackendLinesQueryBehavior: GroupedBackendLinesQueryBehaviorType =
  {
    query: doGroupedBackendLinesApiQuery,
    unpack: (result: GroupedBackendLinesQueryResult) =>
      result?.data?.project?.groupedRuns,
    postProcess: null,
    applyDeltaFn: (vars: {
      currentRunsById: Record<string, BucketedLine>;
      runs: GroupedBackendLinesQueryRuns | null | undefined;
      specs: QueryVars['bucketedHistorySpecs'];
    }) => {
      // The Project.GroupedRuns resolver returns GroupedRuns if it's a supported use case or Runs if it's not. If
      // it is, we can use the grouped delta function. Otherwise we fall back to the same behavior as the non-grouped
      // BackendLinesQueryBehavior (applyBackendLinesDelta), since it will have returned non-aggregated line data.
      const runs = vars.runs;
      if (isAggregatedRuns(runs)) {
        return applyGroupedBackendLinesDelta({
          currentRunsById: vars.currentRunsById,
          order: runs.deltas?.order ?? [],
          delta: runs.deltas?.delta ?? [],
        });
      }
      // This type nastiness is because vars.runs?.deltas?.delta could technically be a GroupedRun delta or a
      // Run delta, even though the GroupedRun case is ruled out by the if (isAggregatedRuns(runs)) above.
      const deltas =
        (vars.runs?.deltas?.delta as NonNullable<
          NonNullable<
            NonNullable<BackendLinesDeltaQueryQuery['project']>['runs']
          >['deltas']
        >['delta']) ?? [];
      const order = vars.runs?.deltas?.order ?? [];
      return applyBackendLinesDelta({
        currentRunsById: vars.currentRunsById,
        order,
        delta: deltas,
      });
    },
  };

type BucketedQueryBehaviorType = BaseQueryBehavior<
  BucketedQueryResult,
  BucketedQueryRuns
>;

export const BucketedQueryBehavior: BucketedQueryBehaviorType = {
  query: doBucketedApiQuery,
  unpack: (result: BucketedQueryResult) => {
    // Only merge if baseline or pinned runs are present (feature flag gated)
    if (
      result?.data?.project?.baselineRun ||
      result?.data?.project?.pinnedRuns
    ) {
      mergeBaselineAndPinnedRuns(result);
    }
    return result?.data?.project?.runs;
  },
  postProcess: postProcessRuns,
  applyDeltaFn: (vars: {
    currentRunsById: Record<string, BucketedLine>;
    runs: BucketedQueryRuns | null | undefined;
    specs: QueryVars['bucketedHistorySpecs'];
  }) => {
    const deltas = vars.runs?.deltas?.delta ?? [];
    const order = vars.runs?.deltas?.order ?? [];
    return applyDelta({
      currentRunsById: vars.currentRunsById,
      order,
      delta: deltas,
      specs: vars.specs,
    });
  },
};

export const BucketedQueryWithInternalIdBehavior: BucketedQueryBehaviorType = {
  query: doBucketedApiQueryByInternalId,
  unpack: (result: BucketedQueryResult) => {
    // Only merge if baseline or pinned runs are present (feature flag gated)
    if (
      result?.data?.project?.baselineRun ||
      result?.data?.project?.pinnedRuns
    ) {
      mergeBaselineAndPinnedRuns(result);
    }
    return result?.data?.project?.runs;
  },
  postProcess: postProcessRuns,
  applyDeltaFn: (vars: {
    currentRunsById: Record<string, BucketedLine>;
    runs: BucketedQueryRuns | null | undefined;
    specs: QueryVars['bucketedHistorySpecs'];
  }) => {
    const deltas = vars.runs?.deltas?.delta ?? [];
    const order = vars.runs?.deltas?.order ?? [];
    return applyDelta({
      currentRunsById: vars.currentRunsById,
      order,
      delta: deltas,
      specs: vars.specs,
    });
  },
};

type GroupedQueryBehaviorType = BaseQueryBehavior<
  GroupedBucketedQueryResult,
  GroupedBucketedQueryRuns
>;

export const GroupedBucketedQueryBehavior: GroupedQueryBehaviorType = {
  query: doGroupedBucketedApiQuery,
  unpack: (result: GroupedBucketedQueryResult) =>
    result?.data?.project?.groupedRuns,
  postProcess: (runs: GroupedBucketedQueryRuns | null | undefined) => {
    if (runs?.__typename === 'GroupedRunConnection') {
      return postProcessGroupedRuns(runs);
    }
    return postProcessRuns(runs);
  },
  applyDeltaFn: (vars: {
    currentRunsById: Record<string, BucketedLine>;
    runs: GroupedBucketedQueryRuns | null | undefined;
    specs: QueryVars['bucketedHistorySpecs'];
  }) => {
    const runs = vars.runs;
    if (isAggregatedRuns(runs)) {
      return applyGroupedDelta({
        currentRunsById: vars.currentRunsById,
        order: runs.deltas?.order ?? [],
        delta: runs.deltas?.delta ?? [],
        specs: vars.specs,
      });
    }

    return applyDelta({
      currentRunsById: vars.currentRunsById,
      order: runs?.deltas?.order ?? [],
      delta: runs?.deltas?.delta ?? [],
      specs: vars.specs,
    });
  },
};

export const GroupedBucketedQueryWithInternalIdBehavior: GroupedQueryBehaviorType =
  {
    query: doGroupedBucketedApiQueryByInternalId,
    unpack: (result: GroupedBucketedQueryResult) =>
      result?.data?.project?.groupedRuns,
    postProcess: (runs: GroupedBucketedQueryRuns | null | undefined) => {
      if (runs?.__typename === 'GroupedRunConnection') {
        return postProcessGroupedRuns(runs);
      }
      return postProcessRuns(runs);
    },
    applyDeltaFn: (vars: {
      currentRunsById: Record<string, BucketedLine>;
      runs: GroupedBucketedQueryRuns | null | undefined;
      specs: QueryVars['bucketedHistorySpecs'];
    }) => {
      const runs = vars.runs;
      if (isAggregatedRuns(runs)) {
        return applyGroupedDelta({
          currentRunsById: vars.currentRunsById,
          order: runs.deltas?.order ?? [],
          delta: runs.deltas?.delta ?? [],
          specs: vars.specs,
        });
      }

      return applyDelta({
        currentRunsById: vars.currentRunsById,
        order: runs?.deltas?.order ?? [],
        delta: runs?.deltas?.delta ?? [],
        specs: vars.specs,
      });
    },
  };
